(function () {
  'use strict';

  function debounce(fn, delay) {
    var timeoutId = null;
    return function () {
      var context = this;
      var args = arguments;
      clearTimeout(timeoutId);
      timeoutId = setTimeout(function () {
        fn.apply(context, args);
      }, delay);
    };
  }

  function escapeHtml(value) {
    if (value == null) return '';
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function normalizeEndpoint(url) {
    var endpoint = (url || '/search/suggest').trim();
    if (!endpoint.endsWith('.json')) {
      endpoint += '.json';
    }
    return endpoint;
  }

  function getProducts(data) {
    if (!data || !data.resources || !data.resources.results) {
      return [];
    }
    var products = data.resources.results.products;
    return Array.isArray(products) ? products : [];
  }

  function setupPredictiveSearch(root, index) {
    var input = root.querySelector('[data-predictive-input]');
    var panel = root.querySelector('[data-predictive-panel]');
    var list = root.querySelector('[data-predictive-list]');
    var status = root.querySelector('[data-predictive-status]');
    var combobox = root.querySelector('[role="combobox"]') || input;
    var trigger = root.querySelector('[data-predictive-trigger]');
    var closeButton = root.querySelector('[data-predictive-close]');
    var form = root.querySelector('[data-predictive-form]');
    if (!input || !panel || !list || !status || !combobox) return;

    var endpoint = normalizeEndpoint(root.getAttribute('data-predictive-url'));
    var limit = parseInt(root.getAttribute('data-limit') || '6', 10);
    var loadingText = root.getAttribute('data-loading-text') || 'Loading...';
    var emptyText = root.getAttribute('data-empty-text') || 'No products found';
    var enterFirst = root.getAttribute('data-enter-first') === 'true';
    var isCollapsible = root.getAttribute('data-collapsible') === 'true';
    var requestController = null;
    var activeIndex = -1;
    var openAriaLabel = trigger ? trigger.getAttribute('data-predictive-open-label') : '';
    var closeAriaLabel = trigger ? trigger.getAttribute('data-predictive-close-label') : '';

    function setTriggerLabel(isOpen) {
      if (!trigger) return;
      if (isOpen && closeAriaLabel) {
        trigger.setAttribute('aria-label', closeAriaLabel);
      } else if (!isOpen && openAriaLabel) {
        trigger.setAttribute('aria-label', openAriaLabel);
      }
    }

    function openRoot() {
      if (!isCollapsible) return;
      root.classList.add('is-open');
      setTriggerLabel(true);
    }

    function closeRoot() {
      if (!isCollapsible) return;
      root.classList.remove('is-open');
      setTriggerLabel(false);
      input.setAttribute('aria-activedescendant', '');
      activeIndex = -1;
    }

    function closePanel() {
      panel.hidden = true;
      combobox.setAttribute('aria-expanded', 'false');
      input.setAttribute('aria-activedescendant', '');
      activeIndex = -1;
    }

    function openPanel() {
      panel.hidden = false;
      combobox.setAttribute('aria-expanded', 'true');
    }

    function suggestionLinks() {
      return panel.querySelectorAll('.sigma-search__suggestion-link');
    }

    function setActive(nextIndex) {
      var links = suggestionLinks();
      if (!links.length) {
        activeIndex = -1;
        input.setAttribute('aria-activedescendant', '');
        return;
      }

      if (nextIndex < 0) {
        nextIndex = links.length - 1;
      } else if (nextIndex >= links.length) {
        nextIndex = 0;
      }

      activeIndex = nextIndex;
      links.forEach(function (link, idx) {
        var isActive = idx === activeIndex;
        link.classList.toggle('is-active', isActive);
        link.setAttribute('aria-selected', isActive ? 'true' : 'false');
        if (isActive) {
          input.setAttribute('aria-activedescendant', link.id);
        }
      });
    }

    function renderEmpty(message) {
      list.innerHTML = '<li class="sigma-search__suggestion-empty">' + escapeHtml(message) + '</li>';
      status.textContent = message;
      openPanel();
    }

    function renderResults(products) {
      if (!products.length) {
        renderEmpty(emptyText);
        return;
      }

      var html = '';
      products.forEach(function (product, itemIndex) {
        var id = 'SigmaPredictiveOption-' + index + '-' + itemIndex;
        var title = escapeHtml(product.title);
        var productUrl = escapeHtml(product.url || '#');
        var image = product.featured_image;
        var price = product.price;
        var priceHtml = '';
        var imageHtml =
          '<span class="sigma-search__suggestion-thumb" aria-hidden="true"></span>';

        if (image && image.url) {
          imageHtml =
            '<span class="sigma-search__suggestion-thumb" aria-hidden="true">' +
            '<img src="' +
            escapeHtml(image.url) +
            '" alt="" loading="lazy" width="72" height="72">' +
            '</span>';
        }

        if (typeof price === 'string' && price.trim() !== '') {
          priceHtml = '<p class="sigma-search__suggestion-price">' + escapeHtml(price) + '</p>';
        }

        html +=
          '<li role="presentation">' +
          '<a id="' +
          id +
          '" role="option" aria-selected="false" class="sigma-search__suggestion-link" href="' +
          productUrl +
          '">' +
          imageHtml +
          '<span class="sigma-search__suggestion-copy">' +
          '<p class="sigma-search__suggestion-title">' +
          title +
          '</p>' +
          priceHtml +
          '</span>' +
          '</a>' +
          '</li>';
      });

      list.innerHTML = html;
      status.textContent = products.length + ' suggestions available';
      activeIndex = -1;
      openPanel();
    }

    function buildUrl(term) {
      var params = new URLSearchParams();
      params.set('q', term);
      params.set('resources[type]', 'product');
      params.set('resources[limit]', String(limit));
      params.set('options[unavailable_products]', 'hide');
      params.set('options[prefix]', 'last');
      return endpoint + '?' + params.toString();
    }

    function fetchSuggestions(term) {
      if (requestController) {
        requestController.abort();
      }
      requestController = new AbortController();
      var signal = requestController.signal;

      status.textContent = loadingText;
      if (!panel.hidden) {
        list.innerHTML = '<li class="sigma-search__suggestion-empty">' + escapeHtml(loadingText) + '</li>';
      }

      return fetch(buildUrl(term), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: signal,
      })
        .then(function (response) {
          if (!response.ok) {
            throw new Error('Predictive search request failed');
          }
          return response.json();
        })
        .then(function (data) {
          renderResults(getProducts(data));
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          console.warn('[sigma-predictive-search] Request failed:', error);
          closePanel();
        })
        .finally(function () {
          requestController = null;
        });
    }

    var debouncedSearch = debounce(function () {
      var term = input.value.trim();
      if (term.length < 2) {
        closePanel();
        return;
      }
      fetchSuggestions(term);
    }, 180);

    input.addEventListener('input', function () {
      if (isCollapsible && !root.classList.contains('is-open')) {
        openRoot();
      }
      debouncedSearch();
    });

    input.addEventListener('keydown', function (event) {
      var links = suggestionLinks();
      if (!links.length || panel.hidden) {
        if (event.key === 'Escape') closePanel();
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActive(activeIndex + 1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActive(activeIndex - 1);
      } else if (event.key === 'Enter' && activeIndex >= 0) {
        event.preventDefault();
        window.location.assign(links[activeIndex].href);
      } else if (event.key === 'Enter' && enterFirst && links.length) {
        event.preventDefault();
        window.location.assign(links[0].href);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        closePanel();
        closeRoot();
        if (trigger && typeof trigger.focus === 'function') {
          trigger.focus();
        }
      }
    });

    document.addEventListener('click', function (event) {
      if (!root.contains(event.target)) {
        closePanel();
        closeRoot();
      }
    });

    input.addEventListener('focus', function () {
      openRoot();
      var term = input.value.trim();
      if (term.length >= 2 && list.children.length > 0) {
        openPanel();
      }
    });

    if (trigger) {
      setTriggerLabel(root.classList.contains('is-open'));
      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        if (root.classList.contains('is-open')) {
          closePanel();
          closeRoot();
          return;
        }
        openRoot();
        if (typeof input.focus === 'function') {
          input.focus();
        }
      });
    }

    if (closeButton) {
      closeButton.addEventListener('click', function () {
        closePanel();
        closeRoot();
        if (trigger && typeof trigger.focus === 'function') {
          trigger.focus();
        }
      });
    }

    if (form) {
      form.addEventListener('submit', function (event) {
        var links = suggestionLinks();
        if (enterFirst && links.length) {
          event.preventDefault();
          var link = activeIndex >= 0 ? links[activeIndex] : links[0];
          window.location.assign(link.href);
          return;
        }
        event.preventDefault();
      });
    }
  }

  function init() {
    var roots = document.querySelectorAll('[data-predictive-search-root]');
    roots.forEach(function (root, index) {
      setupPredictiveSearch(root, index);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
